/* ---------------------------------------------------------------------------
   Nepal, Department of Roads — the deployment this application was written for.

   Everything here was hard-coded somewhere in src/ until tenancy existed, and
   this file is that scattering gathered into one place rather than anything new:
   the agency block came out of src/data/seed.ts, the bounds and outline out of
   src/data/nepal.ts, the road-code rules out of `routeClass` in
   src/data/consequence.ts, and the summary's three groupings out of
   src/data/summary.ts. Nothing about DoR's behaviour changed in the move, which
   is the property `npm test` checks: the same road codes classify the same way
   and the same groupings produce the same rollups.

   THE ONE THING TO BE CAREFUL WITH is `ROAD_CLASSES`. Those four prefixes are
   Nepal's road classification and their order is load-bearing — `/^PR?\d/`
   matches both `P1` and `PR1`, and it has to be tried after `/^F\d/` for no
   reason except that it always was. Leave the order alone unless a DoR document
   says otherwise.
   --------------------------------------------------------------------------- */

import { NEPAL_BOUNDS, NEPAL_OUTLINE } from '../../data/nepal.ts';
import { classifyRoad } from '../roads.ts';
import type { RoadClassRule, TenantProfile } from '../types.ts';

/**
 * Nepal's road classification, from the code a route carries.
 *
 * Named rather than inlined so the `class` grouping below can close over the
 * same array the profile publishes — one list, used by the classifier and by
 * the summary, with no way for the two to disagree.
 */
const ROAD_CLASSES: readonly RoadClassRule[] = [
  { match: /^H\d/, label: 'National highway' },
  { match: /^F\d/, label: 'Feeder road' },
  { match: /^PR?\d/, label: 'Provincial road' },
  { match: /^D\d/, label: 'District road' },
];

export const NEPAL_DOR: TenantProfile = {
  id: 'nepal-dor',
  name: 'Nepal — Department of Roads',
  country: 'NP',
  countryName: 'Nepal',

  agency: {
    government: 'Government of Nepal',
    ministry: 'Ministry of Physical Infrastructure & Transport',
    department: 'Department of Roads',
    departmentShort: 'DoR',
    branch: 'Bridge Branch',
    unit: 'Project Coordination Unit (PCU), Chakupat, Lalitpur',
    unitShort: 'Project Coordination Unit, Chakupat',
    seal: 'NP',
  },

  brand: {
    emblems: [],
    logo: null,
    /* The flag's border blue and crimson. */
    ground: '#003893',
    stripe: ['#dc143c', '#003893'],
  },

  geo: {
    bounds: NEPAL_BOUNDS,
    outline: NEPAL_OUTLINE,
    outlineProvenance:
      'Natural Earth 1:10m Admin 0 Countries, Nepal point-of-view edition ' +
      '(ne_10m_admin_0_countries_nep), public domain, simplified to 272 points by ' +
      'Douglas–Peucker at a 0.0126° tolerance. A cartographic outline at country scale, ' +
      'not a survey boundary. It differs from the default edition in six vertices of ' +
      '1022, all in one patch of the far north-west — see the header of src/data/nepal.ts.',
  },

  /* DoR's own words, off its inventory sheet. `locality` is VDC rather than
     "ward" or "municipality" because that is what the sheet's left-bank and
     right-bank rows are headed, and those two fields exist to match the paper
     somebody is holding beside the screen. */
  vocab: {
    area: 'District',
    areaPlural: 'Districts',
    region: 'Province',
    office: 'Division road office',
    locality: 'VDC',
    road: 'Road',
    chainage: 'Chainage',
  },

  chainage: 'km',

  roadClasses: ROAD_CLASSES,

  /* None. Every field on DoR's inventory sheet has a real column in `bridges` —
     they were added in migration 0006, transcribed from the sheet itself. An
     empty extras list is the healthy state for a tenant, and the one the others
     should be migrated towards. */
  extras: [],

  /* The three ways DoR is actually organised. `district` is the one every
     record has and so it leads; `class` is derived from the route code rather
     than stored, which is why it is a grouping and not a column. */
  groupings: [
    { key: 'district', label: 'District', of: (b) => b.district || null },
    { key: 'division', label: 'Division road office', of: (b) => b.divisionOffice || null },
    { key: 'class', label: 'Road classification', of: (b) => classifyRoad(b.route, ROAD_CLASSES) },
  ],

  currency: {
    code: 'NPR',
    symbol: 'Rs',
    unit: 'rupee',
    fiscalPeriodLabel: 'Fiscal year (Bikram Sambat, e.g. 2081/82)',
  },

  /* Nepali is offered and is honest about its coverage — see the header of
     src/data/locale.ts, which explains what is translated, what deliberately is
     not, and why the language control reports a percentage. */
  locales: ['en', 'ne'],

  /* NBC 105:2020, Nepal's seismic code. Its zoning factor is municipality-level
     data this register does not carry, and the second half is the part that
     matters for how the screen is read: with no low-hazard zone anywhere in the
     country, a hazard screen could not separate these structures from one
     another even with the map. So the screen ranks vulnerability and says so. */
  seismicZoningNote:
    "NBC 105:2020's zoning factor is municipality-level data the register does not hold, " +
    'and no part of Nepal is a low-hazard zone.',

  registerProvenanceShort: 'All geometry from as-built GA drawings',

  /* Shown to a reader on the Options screen, so it says "not recorded" rather
     than "null": the person who most needs to know how much of a register is
     unrecorded is the least likely to read a null as an absence. */
  registerProvenance:
    'Five structures, each transcribed from as-built General Arrangement and Cross ' +
    'Section drawings held in the project folder. The geometry is real; the ' +
    'inventory-sheet fields a drawing does not carry are left unrecorded rather than ' +
    'estimated. Coordinates are Google Maps pins, not surveyed positions.',

  /* PROVISIONAL (LC-24). DoR publishes no re-rating interval; five years
     follows the principal-inspection cycle, and two for a posted bridge. */
  rerating: { years: 5, restrictedYears: 2 },

  /* PROVISIONAL (LC-61). Annual routine and five-yearly principal, as the
     cycle always ran; six months and three years for a restricted bridge or
     one whose latest condition is 3 of 4 or worse. */
  inspection: { routineMonths: 12, principalMonths: 60, shortRoutineMonths: 6, shortPrincipalMonths: 36, poorFrom: 0.75 },
};
